import { makeStyles } from '@material-ui/core/styles';

import { CARD, KICKER, PILL_SECONDARY, TEXT_LINK } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

export const ONE_COL = '@media (max-width: 760px)';

// Styles the four about sub-pages share: the section heading, the two-column
// text/visual row on a card, the framed embed, quotes and external links.
const useAboutStyles = makeStyles(() => ({
    head: {
        maxWidth: '720px',
        margin: '0 auto 32px',
        textAlign: 'center',
    },
    h2: {
        margin: '0 0 12px',
        fontSize: 'clamp(1.7rem, 3.2vw, 2.5rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    lead: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '1.04rem',
        lineHeight: 1.6,
    },
    kickerRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        margin: '0 0 18px',
    },
    kicker: {
        ...KICKER,
        color: REDESIGN_COLORS.accent,
    },
    kickerRule: {
        flex: 1,
        height: '1px',
        background: REDESIGN_COLORS.ruleSand,
    },
    card: {
        ...CARD,
        padding: 'clamp(22px, 2.6vw, 34px)',
        marginBottom: '22px',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
        gap: 'clamp(22px, 3vw, 40px)',
        alignItems: 'center',
        [ONE_COL]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    rowFlipped: {
        gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1.15fr)',
        '& > :first-child': {
            order: 2,
        },
        [ONE_COL]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
            '& > :first-child': {
                order: 0,
            },
        },
    },
    h3: {
        margin: '0 0 10px',
        fontSize: 'clamp(1.3rem, 2.2vw, 1.7rem)',
        lineHeight: 1.15,
        letterSpacing: '-0.025em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: '0 0 12px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.98rem',
        lineHeight: 1.6,
        '&:last-child': {
            marginBottom: 0,
        },
    },
    quote: {
        margin: '0 0 12px',
        padding: '2px 0 2px 16px',
        borderLeft: `3px solid ${REDESIGN_COLORS.highlight}`,
        color: REDESIGN_COLORS.dark,
        fontSize: '0.95rem',
        lineHeight: 1.55,
        fontStyle: 'italic',
        '& cite': {
            color: REDESIGN_COLORS.muted,
            fontStyle: 'normal',
            fontSize: '0.88rem',
        },
    },
    links: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '16px',
    },
    external: {
        ...PILL_SECONDARY,
        minHeight: '42px',
        padding: '0 18px',
        fontSize: '0.9rem',
    },
    textLink: TEXT_LINK,
    // A photo or illustration on a tinted mat.
    mat: {
        display: 'grid',
        placeItems: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden',
        padding: '18px',
        borderRadius: '20px',
    },
    photo: {
        display: 'block',
        width: '100%',
        aspectRatio: '4 / 3',
        objectFit: 'cover',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(6, 63, 69, 0.06), 0 12px 32px rgba(6, 63, 69, 0.09)',
    },
    logo: {
        display: 'block',
        maxWidth: '240px',
        width: '70%',
        height: 'auto',
    },
    // A video embed in the landing's white frame.
    frame: {
        boxSizing: 'border-box',
        padding: 'clamp(10px, 1.4vw, 16px)',
        borderRadius: '20px',
        background: REDESIGN_COLORS.mist,
    },
    // A 16:9 box drawn with padding-top rather than aspect-ratio: browsers before
    // Chrome 88 (older interactive whiteboards) ignore aspect-ratio, which
    // squashed these embeds to the iframe default of 150px.
    ratio: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        background: REDESIGN_COLORS.white,
        boxShadow: '0 1px 2px rgba(6, 63, 69, 0.06), 0 16px 40px rgba(6, 63, 69, 0.12)',
        '&::before': { content: '""', display: 'block', paddingTop: '56.25%' },
    },
    embed: {
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'block',
        width: '100%',
        height: '100%',
        border: 0,
    },
}));

export default useAboutStyles;
